import { Globe, Users, ShieldCheck, Lock, Check, Landmark } from 'lucide-react'
import { useSession } from '@/context/AuthContext'
import { useRegisteredUsers } from '@/lib/useRegisteredUsers'
import { canChooseVisibility, normalizeVisibility, visibilityNeedsViewers, type Visibility } from '@/lib/roles'
import { picColor } from '@/lib/utils'

const OPTIONS = [
  { value: 'public', label: 'Semua', Icon: Globe, hint: 'Semua anggota unit bisnis bisa lihat' },
  // Tier 'subhead' sengaja TIDAK ditawarkan lagi: batas confidential kini Head ke atas,
  // jadi ia berperilaku persis 'head'. Baris lama ('subhead'/'managerial'/'confidential')
  // tetap tersimpan & dirender oleh VisibilityBadge di bawah.
  { value: 'head', label: 'Head+', Icon: ShieldCheck, hint: 'Head & Director saja' },
  { value: 'custom', label: 'Orang tertentu', Icon: Lock, hint: 'Hanya orang yang kamu pilih (Head & Director tetap bisa lihat)' },
  { value: 'director', label: 'Direksi', Icon: Landmark, hint: 'Hanya Director & orang yang kamu pilih. Head BU pun tidak melihat. Untuk data sensitif (HR/Finance).' },
] as const

const HINT: Record<Visibility, string> = {
  public: 'Semua anggota unit bisnismu bisa melihat ini.',
  subhead: 'Hanya Head & Director yang melihatnya. (Tingkat lama, kini setara Head+.)',
  head: 'Hanya Head & Director yang melihatnya.',
  custom: 'Hanya orang yang kamu pilih. Kamu, PIC, Head, dan Director tetap bisa melihat.',
  director: 'Tingkat brankas: hanya Director dan orang yang kamu pilih. Head BU pun tidak melihat.',
}

/**
 * Pemilih siapa yang boleh melihat item (project / ide / konten / ticket).
 * Tidak dirender untuk Staff tanpa grant can_set_visibility — mereka selalu terbuka se-BU.
 */
export function VisibilityPicker({
  value,
  onChange,
  viewerEmails,
  onViewerEmailsChange,
  legend = 'Siapa yang bisa melihat',
}: {
  value: Visibility
  onChange: (v: Visibility) => void
  viewerEmails: string[]
  onViewerEmailsChange: (emails: string[]) => void
  legend?: string
}) {
  const { data: session } = useSession()
  const { users } = useRegisteredUsers()
  const myEmail = (session?.user?.email ?? '').toLowerCase()

  if (!canChooseVisibility(session?.user?.role, session?.user?.canSetVisibility)) return null

  const selected = new Set(viewerEmails.map((e) => e.toLowerCase()))
  const toggle = (email: string) => {
    const e = email.toLowerCase()
    onViewerEmailsChange(
      selected.has(e) ? viewerEmails.filter((x) => x.toLowerCase() !== e) : [...viewerEmails, e],
    )
  }
  const others = users.filter((u) => u.email.toLowerCase() !== myEmail)

  return (
    <fieldset>
      <legend className="block text-sm font-semibold text-[#111110] font-body mb-1.5">{legend}</legend>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map(({ value: val, label, Icon, hint }) => (
          <button
            key={val}
            type="button"
            onClick={() => onChange(val)}
            aria-pressed={value === val}
            title={hint}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-body font-semibold min-h-[44px] transition-colors ${
              value === val
                ? 'bg-[#111110] text-white border-[#111110]'
                : 'bg-white text-[#6B6860] border-[#E0DFD8] hover:border-[#C8C7BF]'
            }`}
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" /> {label}
          </button>
        ))}
      </div>

      {visibilityNeedsViewers(value) && (
        <div className="mt-2 border border-[#E0DFD8] rounded-xl max-h-44 overflow-auto bg-white">
          {others.length === 0 && (
            <p className="px-3.5 py-2.5 text-xs text-[#AEADA8] font-body">Belum ada user lain di unit bisnismu.</p>
          )}
          {others.map((u) => {
            const on = selected.has(u.email.toLowerCase())
            return (
              <button
                key={u.email}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggle(u.email)}
                className={`w-full flex items-center gap-2 px-3.5 py-2 min-h-[44px] text-sm font-body transition-colors ${
                  on ? 'bg-[#F4F3F0] text-[#111110] font-semibold' : 'text-[#6B6860] hover:bg-[#F8F7F3]'
                }`}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: picColor(u.username) }}
                >
                  {u.username.charAt(0).toUpperCase()}
                </span>
                <span className="truncate flex-1 text-left">{u.username}</span>
                {on && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              </button>
            )
          })}
        </div>
      )}

      <p className="text-[11px] text-[#AEADA8] font-body mt-1.5">
        {HINT[value]}
        {visibilityNeedsViewers(value) && !viewerEmails.length && (
          <span className="text-[#DC2626]"> Pilih minimal satu orang.</span>
        )}
      </p>
    </fieldset>
  )
}

/** Penanda item yang tidak terbuka untuk semua. */
export function VisibilityBadge({ v }: { v?: string | null }) {
  const vis = normalizeVisibility(v)
  if (vis === 'public') return null
  // 'subhead' (termasuk legacy 'managerial'/'confidential') kini setara Head+ — beri
  // label yang jujur, jangan menyebut Sub-Head yang justru tak boleh melihatnya.
  const label = vis === 'director' ? 'Direksi' : vis === 'custom' ? 'Terbatas' : 'Head+'
  const Icon = vis === 'director' ? Landmark : vis === 'custom' ? Lock : ShieldCheck
  return (
    <span
      title={HINT[vis]}
      className="inline-flex items-center gap-1 text-[9px] font-body font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] flex-shrink-0"
    >
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  )
}
